class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Function to refresh the cart drawer
async function refreshCartDrawer() {
  const cartDrawer = document.querySelector("cart-drawer, [id*='cart-drawer'], [class*='cart-drawer']");
  if (!cartDrawer) return;

  try {
    const html = await (await fetch("/cart?section_id=cart-drawer")).text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const newInner = doc.querySelector(".drawer__inner, .cart-drawer__content, [data-cart-drawer-content]");
    const currentInner = cartDrawer.querySelector(".drawer__inner, .cart-drawer__content, [data-cart-drawer-content]");

    if (newInner && currentInner) {
      currentInner.innerHTML = newInner.innerHTML;
      cartDrawer.classList.remove("is-empty");
      customElements?.upgrade?.(cartDrawer);
    }
  } catch (err) {
    console.error("Error refreshing cart drawer:", err);
  }
}

// Function to open the cart drawer
function openCartDrawer() {
  const cartDrawer = document.querySelector("cart-drawer");
  if (!cartDrawer) return;
  
  if (typeof cartDrawer.open === "function") {
    cartDrawer.open();
  } else {
    cartDrawer.classList.add("is-open", "active", "open");
    document.body.classList.add("overflow-hidden");
  }
}

function showCartDrawerError(message) {
  const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
  if (!errors) return;

  errors.textContent = message || '';
}

function closeConfiguratorOverlay() {
  const iframeOverlay = document.getElementById('iframeOverlay');
  const iframe = document.getElementById('customizeIframe');

  if (!iframeOverlay || !iframe) return;

  if (typeof window.closePoubelConfigurator === 'function') {
    window.closePoubelConfigurator();
    return;
  }

  iframeOverlay.style.display = 'none';
  document.body.style.overflow = '';
  document.documentElement.classList.remove('iframe-open');
  document.body.classList.remove('iframe-open');
  iframe.src = 'about:blank';
}

function setConfiguratorLoading(isLoading) {
  const loader = document.getElementById('iframeLoader');
  const iframe = document.getElementById('customizeIframe');

  if (loader) {
    loader.style.display = isLoading ? 'flex' : 'none';
  }

  if (iframe) {
    iframe.style.pointerEvents = isLoading ? 'none' : '';
  }
}

// Listener for iframe configurator messages
window.addEventListener('message', async (event) => {
  if (event.data?.type === 'addToCartFromConfigurator') {
    const { products } = event.data.data;

    try {
      setConfiguratorLoading(true);
      showCartDrawerError('');

      const errors = [];
      let addedItemsCount = 0;

      for (const product of products) {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: product.id,
                quantity: product.quantity,
                properties: {
                  ...(product.properties || {}),
                  _customisation: 'True',
                },
              },
            ],
          }),
        });

        const responseData = await response.json();

        if (!response.ok || responseData.status) {
          errors.push(responseData.description || responseData.message || window.cartStrings?.error || 'Unable to add products to cart.');
          continue;
        }

        addedItemsCount += 1;
      }

      closeConfiguratorOverlay();

      if (addedItemsCount > 0) {
        await refreshCartDrawer();
        openCartDrawer();
      }

      if (errors.length > 0) {
        showCartDrawerError(errors.join('\n'));

        if (addedItemsCount === 0) {
          openCartDrawer();
        }
      } else {
        showCartDrawerError('');
      }

    } catch (error) {
      closeConfiguratorOverlay();
      showCartDrawerError(error.message || window.cartStrings?.error || 'Unable to add products to cart.');
      openCartDrawer();
      console.error('Error adding products to cart:', error);
    } finally {
      setConfiguratorLoading(false);
    }
  }
});

async function waitForSwym(maxAttempts = 20) {
  let attempts = 0;

  while (!window._swat && attempts < maxAttempts) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    attempts++;
  }

  return window._swat;
}

function notifyConfiguratorWishlistResult(type) {
  document.getElementById('customizeIframe')?.contentWindow?.postMessage({ type }, '*');
}

function showWishlistDrawerAfterConfigurator() {
  document.dispatchEvent(new CustomEvent('poubel:wishlist-changed'));

  // Same as the cart flow: dismiss the configurator before showing the drawer.
  // iframe-open also blocks touchmove on mobile, so it must be cleared.
  closeConfiguratorOverlay();
  document.documentElement.classList.remove('iframe-open');
  document.body.classList.remove('iframe-open');

  if (typeof window.openWishlistDrawer === 'function') {
    window.openWishlistDrawer();
  } else {
    console.error('Wishlist drawer is not available');
  }
}

window.addEventListener('message', async (event) => {
  if (event.data?.type !== 'addToWishlistFromConfigurator') {
    return;
  }

  let succeeded = false;

  try {
    const swat = await waitForSwym();

    if (!swat) {
      console.error('Swym is not loaded');
      return;
    }

    const { products } = event.data.data || {};

    if (!Array.isArray(products) || !products.length) {
      return;
    }

    let allAdded = true;

    for (const product of products) {
      const payload = {
        empi: Number(product.empi),
        epi: Number(product.epi),
        du: product.productUrl
      };

      // Swym can swallow a 500 without calling either callback, so never wait forever.
      const added = await new Promise((resolve) => {
        const timeout = setTimeout(() => {
          console.error('Wishlist add timed out', payload);
          resolve(false);
        }, 5000);

        swat.addToWishList(
          payload,
          function (response) {
            clearTimeout(timeout);
            console.log('Wishlist item added', payload, response);
            resolve(true);
          },
          function (error) {
            clearTimeout(timeout);
            console.error('Wishlist add failed', payload, error);
            resolve(false);
          }
        );
      });

      if (!added) allAdded = false;
    }

    succeeded = allAdded;
  } catch (error) {
    console.error('Wishlist from configurator failed', error);
  } finally {
    // Tell the configurator first: closing the overlay reloads the iframe.
    notifyConfiguratorWishlistResult(succeeded ? 'wishlistSaved' : 'wishlistFailed');

    if (succeeded) {
      showWishlistDrawerAfterConfigurator();
    }
  }
});

// Auto-close cart drawer when cart is empty
(() => {

  const getDrawer = () =>
    document.querySelector('cart-drawer, .cart-drawer, #CartDrawer, [data-cart-drawer]');

  const closeDrawer = (drawer) => {
    if (!drawer) return;

    if (typeof drawer.close === 'function') {
      drawer.close();
    } else {
      drawer.querySelector('.cart-drawer__close, .drawer__close, [data-close-cart]')?.click();
      drawer.classList.remove('is-open', 'open', 'active');
      document.body.classList.remove('drawer-open');
    }
  };

  // Close drawer if no cart items exist
  const checkCart = (cart) => {
    if (!cart || cart.item_count === 0) {
      closeDrawer(getDrawer());
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    const drawer = getDrawer();
    if (!drawer) return;

    // Watch for DOM changes (cart item removed manually)
    new MutationObserver(() => {
      if (!drawer.querySelector('.cart-item, [data-cart-item]')) {
        closeDrawer(drawer);
      }
    }).observe(drawer, { childList: true, subtree: true });
  });

  // Shopify event fires when cart updates
  document.addEventListener('cart:updated', (event) => {
    const cart = event?.detail?.cart;
    checkCart(cart);
  });

})();
